import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Container, Grid, Typography, IconButton, Link, Collapse, useTheme, useMediaQuery, Tooltip, Snackbar, Alert } from "@mui/material";
import { styled } from "@mui/system";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LLLogo from "../assets/images/LL2.png"; 

const StyledFooter = styled("footer")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(2),
  boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.1)",
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: "none",
  transition: "color 0.3s ease",
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "scale(1.1)",
  },
}));

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expandedSections, setExpandedSections] = useState({});

  // Snackbar állapotok
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Ezek a linkek jelenleg disable állapotúak
  const disabledLinks = [
    "Developer",
  ];

  // Footer szekciók
  const footerSections = {
    company: {
      title: "About Us",
      links: [
        { label: "Developer", url: "/developer" },
        { label: "Other Projects", url: "/projects" },
      ],
    },
    support: {
      title: "Support",
      links: [
        { label: "Help Center" },
        { label: "Contact Us" },
      ],
    },
    legal: {
      title: "Legal",
      links: [
        { label: "Privacy Policy", url: "/privacypolicy" },
        { label: "Terms of Service", url: "/termsofservice" },
        { label: "Cookie Policy", url: "/cookiepolicy" },
      ],
    },
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Handler a Snackbar megjelenítéséhez
  const handleShowSnackbar = (message: string) => {
    navigator.clipboard.writeText(message)
      .then(() => {
        setSnackbarMessage(`${message} copied to clipboard!`);
        setSnackbarOpen(true);
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
        setSnackbarMessage('Failed to copy to clipboard.');
        setSnackbarOpen(true);
      });
  };

  return (
    <StyledFooter>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} sm={4} md={3}>
            <Box mb={3}>
              <img
                src={LLLogo}                             
                alt="LinkLearning Logo"
                style={{ height: 80, width: "auto" }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Creating innovative solutions for tomorrow's challenges.
              </Typography>
            </Box>
          </Grid>

          {Object.entries(footerSections).map(([key, section]) => (
            <Grid item xs={12} sm={4} md={2} key={key}>
              <Box>
                <Tooltip title={section.title} >
                  <Typography
                    variant="h6"
                    color="textPrimary"
                    gutterBottom
                    onClick={() => isMobile && toggleSection(key)}
                    sx={{ cursor: isMobile ? "pointer" : "default", display: "flex", alignItems: "center" }}
                  >
                    {section.title}
                    {isMobile && (
                      expandedSections[key] ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />
                    )}
                  </Typography>
                </Tooltip>
                <Collapse in={!isMobile || expandedSections[key]}>
                  {section.links.map((link) => {
                    if (disabledLinks.includes(link.label)) {
                      return (
                        <Box key={link.label} mb={1}>
                          <Tooltip title="Currently this feature is not available" >
                            <Typography variant="body2" color="text.disabled">
                              {link.label}
                            </Typography>
                          </Tooltip>
                        </Box>
                      );
                    } else if (link.label === "Help Center" || link.label === "Contact Us") {
                      return (
                        <Box key={link.label} mb={1}>
                          <Tooltip title={link.label}>
                            <FooterLink
                              href="#"
                              aria-label={link.label}
                              onClick={(e) => {
                                e.preventDefault();
                                handleShowSnackbar("Email: official.linklearning@gmail.com");
                              }}
                            >
                              <Typography variant="body2">{link.label}</Typography>
                            </FooterLink>
                          </Tooltip>
                        </Box>
                      );
                    } else {
                      return (
                        <Box key={link.label} mb={1}>
                          <Tooltip title={link.label}>
                            <FooterLink 
                              component={RouterLink} 
                              to={link.url ? link.url : "#"} 
                              aria-label={link.label}
                            >
                              <Typography variant="body2">{link.label}</Typography>
                            </FooterLink>
                          </Tooltip>
                        </Box>
                      );
                    }
                  })}
                </Collapse>
              </Box>
            </Grid>
          ))}

          <Grid item xs={12} sm={4} md={3}>
            <Typography variant="h6" color="textPrimary" gutterBottom>
              Connect With Us
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="Email" arrow>
                <SocialButton
                  color="primary"
                  aria-label="Mail"
                  onClick={() => handleShowSnackbar("Email: official.linklearning@gmail.com")}
                >
                  <EmailRoundedIcon />
                </SocialButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            borderTop: 1,
            borderColor: "divider",
            mt: 4,
            pt: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="textSecondary">
            © {new Date().getFullYear()} LinkLearning. All rights reserved.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mt: { xs: 2, sm: 0 } }}>
            <Tooltip title="Currently this feature is not available" placement="bottom-start" arrow>
                <span>
                <FooterLink sx={{ pointerEvents: "none" }}>
                    <Typography variant="body2" color="text.disabled">
                    Privacy Policy
                    </Typography>
                </FooterLink>
                </span>
            </Tooltip>
            <Tooltip title="Currently this feature is not available" placement="bottom-start" arrow>
                <span>
                <FooterLink sx={{ pointerEvents: "none" }}>
                    <Typography variant="body2" color="text.disabled">
                    Terms of Service
                    </Typography>
                </FooterLink>
                </span>
            </Tooltip>
            <Tooltip title="Currently this feature is not available" placement="bottom-start" arrow>
                <span>
                <FooterLink sx={{ pointerEvents: "none" }}>
                    <Typography variant="body2" color="text.disabled">
                    Cookie Policy
                    </Typography>
                </FooterLink>
                </span>
            </Tooltip>
            </Box>


        </Box>
      </Container>

      {/* Snackbar a felugró értesítéshez */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledFooter>
  );
};

export default Footer;
